import { useEffect, useMemo, useRef, useState } from "react";

import { useOrders, useRouteMutation } from "@web-app/api";
import {
  AccordionV2,
  Button,
  ConfirmModal,
  DateInput,
  Field,
  Header,
  Icon,
  Input,
  Label,
  SearchWithMultiSelect,
  SelectAssignee,
  Tooltip,
  useAlert,
} from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order } from "@web-app/types";
import { pluralize } from "@web-app/utils/helpers";

import { OrderList } from "./components/OrderList";
import { RouteValidationErrors } from "./components/RouteValidationErrors";
import { useDelayedOrderDetection } from "./hooks/useDelayedOrderDetection";
import { useEarlyDeliveryDetection } from "./hooks/useEarlyDeliveryDetection";
import { useEstimatedPickupTime } from "./hooks/useEstimatedPickupTime";
import { useInvalidTimestampDetection } from "./hooks/useInvalidTimestampDetection";
import { useRouteDrawing } from "./hooks/useRouteDrawing";
import { useRouteValidation } from "./hooks/useRouteValidation";

export default function CreateRoute({
  setIsCreating,
}: {
  setIsCreating: (isCreating: boolean) => void;
}) {
  const { showAlertAsync } = useAlert();

  const [isOptimizeClicked, setIsOptimizeClicked] = useState(false);
  const [name, setName] = useState("");
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [isFlexible, setIsFlexible] = useState(false);
  const [respectTimeWindows, setRespectTimeWindows] = useState(false);
  const [asEarlyAsPossible, setAsEarlyAsPossible] = useState(true);
  const [earlyThresholdMinutes, setEarlyThresholdMinutes] = useState(30);
  const [isOverwriteAssignee, setIsOverwriteAssignee] = useState(false);
  const [showHasDelayedOrdersModal, setShowHasDelayedOrdersModal] =
    useState(false);
  const [showHasEarlyDeliveryOrdersModal, setShowHasEarlyDeliveryOrdersModal] =
    useState(false);
  const [estimatedPickupTime, setEstimatedPickupTime] = useState<Date | null>(
    null,
  );
  const [showReorderWarning, setShowReorderWarning] = useState(false);

  const { createRouteDraft, createRoute } = useRouteMutation();
  const { user: currentUser, editRouteOrderIds } = useDispatchStore();
  const { data } = useOrders();
  const orders = data?.orders;
  const assignees = data?.assignees;

  const hasInitialSortBeenApplied = useRef(false);

  // Sort orders only on initial selection (pickups first)
  useEffect(() => {
    if (hasInitialSortBeenApplied.current) return;

    if (!orders || editRouteOrderIds.length === 0) return;

    const sortedIds = [...editRouteOrderIds].sort((a, b) => {
      const orderA = orders.get(a);
      const orderB = orders.get(b);

      const isPickupA = orderA?.type === "ROUTE_OPT_PICKUP";
      const isPickupB = orderB?.type === "ROUTE_OPT_PICKUP";

      if (isPickupA && !isPickupB) return -1;
      if (!isPickupA && isPickupB) return 1;
      return 0;
    });

    const hasOrderChanged = sortedIds.some(
      (id, index) => id !== editRouteOrderIds[index],
    );

    if (hasOrderChanged) {
      useDispatchStore.setState({
        editRouteOrderIds: sortedIds,
      });
    }

    // Mark initial sort as applied
    hasInitialSortBeenApplied.current = true;
  }, [editRouteOrderIds, orders]);

  const {
    getAssigneeValidationState,
    isAssigneesValid,
    isPickupTimeValid,
    isRouteSequenceValid,
    isSameDateOrders,
    isWorkspaceValid,
  } = useRouteValidation(editRouteOrderIds, orders, estimatedPickupTime);

  const computedEstimatedPickupTime = useEstimatedPickupTime({
    isRouteSequenceValid: isRouteSequenceValid().isValid,
    orderIds: editRouteOrderIds,
    orders,
    routeEstimatedPickupTime: null, // CreateRoute doesn't have an existing route
  });

  const {
    distance,
    durations,
    isOptimizing,
    drawSimpleRoute,
    drawDirections,
    optimizeOrders,
    currentPolylineRef,
    routedOrdersDraftEstimatedETA,
    setRoutedOrdersDraftEstimatedETA,
  } = useRouteDrawing(
    editRouteOrderIds,
    orders,
    currentUser,
    assigneeId,
    name,
    isFlexible,
    respectTimeWindows,
    !asEarlyAsPossible,
    earlyThresholdMinutes,
    estimatedPickupTime,
  );

  const { hasPotentialDelayedOrders } = useDelayedOrderDetection({
    orders,
    routedOrdersDraftEstimatedETA,
  });

  const { invalidTimestampOrders } = useInvalidTimestampDetection({
    orderIds: editRouteOrderIds,
    orders,
  });

  const { hasPotentialEarlyDeliveryOrders } = useEarlyDeliveryDetection({
    orderIds: editRouteOrderIds,
    orders,
    routedOrdersDraftEstimatedETA,
  });

  const hasInvalidTimestamps =
    invalidTimestampOrders && invalidTimestampOrders.length > 0;

  // Update estimatedPickupTime when the computed value changes
  useEffect(() => {
    setEstimatedPickupTime(computedEstimatedPickupTime);
  }, [computedEstimatedPickupTime]);

  useEffect(() => {
    return () => {
      if (currentPolylineRef.current) {
        currentPolylineRef.current.setMap(null);
        currentPolylineRef.current = null;
      }

      // Reset initial sort flag on unmount
      hasInitialSortBeenApplied.current = false;

      useDispatchStore.setState({
        editRouteAssigneeId: null,
        editRouteOrderIds: [],
      });
    };
  }, []);

  const prevEditRouteOrderIdsLengthRef = useRef(editRouteOrderIds.length);

  useEffect(() => {
    if (!assigneeId) {
      const ordersWithAssignees = editRouteOrderIds
        .map((id) => orders?.get(id))
        .filter((order): order is Order => !!order)
        .filter((order) => order.assigneeId);

      const firstAssigneeId = ordersWithAssignees[0]?.assigneeId;
      if (firstAssigneeId) {
        setAssigneeId(firstAssigneeId);
      }
    }

    // Check if this is a bulk change (rectangle selection)
    const lengthDiff = Math.abs(
      editRouteOrderIds.length - prevEditRouteOrderIdsLengthRef.current,
    );
    const isBulkChange = lengthDiff > 1; // More than 1 order changed at once suggests rectangle selection

    // Don't fit bounds if this is a bulk change (rectangle selection)
    drawSimpleRoute(editRouteOrderIds, !isBulkChange);

    prevEditRouteOrderIdsLengthRef.current = editRouteOrderIds.length;
  }, [editRouteOrderIds.length]);

  const moveOrder = (dragIndex: number, hoverIndex: number) => {
    const dragOrder = editRouteOrderIds[dragIndex];
    const newOrders = [...editRouteOrderIds];
    newOrders.splice(dragIndex, 1);
    newOrders.splice(hoverIndex, 0, dragOrder);
    useDispatchStore.setState({ editRouteOrderIds: newOrders });
    drawSimpleRoute(newOrders);
    setShowReorderWarning(true);
  };

  const removeOrder = (orderId: string) => {
    const newOrders = editRouteOrderIds.filter((oid) => oid !== orderId);
    useDispatchStore.setState({ editRouteOrderIds: newOrders });
    drawSimpleRoute(newOrders);
  };

  const ordersArray = useMemo(() => {
    if (!orders?.size) {
      return [];
    }

    const allOrders = Array.from(orders.values());
    const filteredOrders = allOrders.filter((order) => {
      const isUnrouted =
        !order.routeId && order.type !== "INDIVIDUAL_PICKUP_AND_DROPOFF";

      return isUnrouted;
    });

    return filteredOrders;
  }, [orders, editRouteOrderIds]);

  const totalDistance = distance ? distance.toFixed(1) : "";

  const saveRoute = async () => {
    setShowHasDelayedOrdersModal(false);

    // Convert Date to ISO format if provided
    const estimatedPickupDateTime = estimatedPickupTime
      ? estimatedPickupTime.toISOString()
      : null;

    await showAlertAsync(
      createRoute.mutateAsync({
        assigneeId: assigneeId || undefined,
        currentWorkspaceId: currentUser!.currentWorkspaceId,
        estimatedPickupTime: estimatedPickupDateTime,
        externalId: name,
        isFlexible: isFlexible,
        isOptimizeClicked: isOptimizeClicked,
        orderIds: editRouteOrderIds,
      }),
      {
        error: {
          message: "Unable to create route. Please try again",
          title: "Failed!",
        },
        loading: {
          message: "Creating route...",
          title: "Creating",
        },
        success: {
          message: "Successfully created route!",
          title: "Success!",
        },
      },
    ).then(() => {
      useDispatchStore.setState({
        editRouteOrderIds: [],
      });
      setIsCreating(false);
      setRoutedOrdersDraftEstimatedETA(new Map());
    });
  };

  const checkAndCreateRoute = async () => {
    if (hasPotentialDelayedOrders && editRouteOrderIds.length) {
      setShowHasDelayedOrdersModal(true);
      return;
    }

    if (hasPotentialEarlyDeliveryOrders) {
      setShowHasEarlyDeliveryOrdersModal(true);
      return;
    }

    await saveRoute();
  };

  return (
    <div className="absolute bg-white h-[100dvh] lg:h-full w-full z-30 flex flex-col overflow-hidden">
      <Header
        title="Create Route"
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={() => setIsCreating(false)}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto px-4 mt-2">
        <div className="flex flex-col items-center gap-2">
          {/* Name + Assignee row */}
          <div className="flex gap-2 w-full">
            <Field className="flex-1">
              <Label>
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                name="externalId"
                value={name}
                onChange={(value) => setName(value)}
              />
            </Field>
            <Field className="flex-1">
              <Label>Assignee</Label>
              <SelectAssignee
                selectedId={assigneeId}
                onChange={(assigneeId) => {
                  setAssigneeId(assigneeId);
                  useDispatchStore.setState({
                    editRouteAssigneeId: assigneeId,
                  });
                }}
              />
            </Field>
          </div>

          {/* Advanced accordion */}
          <AccordionV2 isBorderless noHoverBg className="w-full">
            <AccordionV2.Label textAlign="left">
              Advanced Options
            </AccordionV2.Label>
            <AccordionV2.Content>
              <div className="flex flex-col gap-2 pt-2 pb-3">
                {estimatedPickupTime && (
                  <Field className="w-full">
                    <Label>Estimated Pickup Time</Label>
                    <DateInput
                      name="estimatedPickupTime"
                      value={estimatedPickupTime}
                      onChange={(value) => {
                        setEstimatedPickupTime(value);
                      }}
                      disabled={editRouteOrderIds.length === 0}
                      position="under"
                      timezone={orders?.get(editRouteOrderIds[0])?.timezone}
                    />
                  </Field>
                )}
                <Field className="w-full">
                  <Input
                    type="toggle"
                    name="isFlexible"
                    value={isFlexible ? "true" : "false"}
                    onChange={(value) => setIsFlexible(value === "true")}
                    rightLabel="Allow out-of-sequence delivery"
                  />
                </Field>
                <div className="w-full border-t border-gray-200 pt-2">
                  <Label>Delivery window constraints</Label>
                </div>
                <Field className="w-full">
                  <Input
                    type="toggle"
                    name="respectTimeWindows"
                    value={respectTimeWindows ? "true" : "false"}
                    onChange={(value) =>
                      setRespectTimeWindows(value === "true")
                    }
                    rightLabel="Respect delivery time windows for ETAs"
                  />
                </Field>
                {!respectTimeWindows && (
                  <>
                    <Field className="w-full">
                      <Input
                        type="toggle"
                        name="asEarlyAsPossible"
                        value={asEarlyAsPossible ? "true" : "false"}
                        onChange={(value) =>
                          setAsEarlyAsPossible(value === "true")
                        }
                        rightLabel="Arrive as early as possible (no threshold)"
                      />
                    </Field>
                    <Field className="self-start" disabled={asEarlyAsPossible}>
                      <Label disabled={asEarlyAsPossible}>
                        Early arrival threshold (minutes)
                      </Label>
                      <Input
                        type="number"
                        name="earlyThresholdMinutes"
                        value={String(earlyThresholdMinutes)}
                        onChange={(value) =>
                          setEarlyThresholdMinutes(Number(value))
                        }
                        min="0"
                        disabled={asEarlyAsPossible}
                        className="w-20"
                      />
                    </Field>
                  </>
                )}
              </div>
            </AccordionV2.Content>
          </AccordionV2>

          {/* Search Orders */}
          <Field className="w-full mt-1">
            <SearchWithMultiSelect
              hideClearButton
              showSearchIcon
              placeholder="Search orders to add to route"
              options={ordersArray.map((order) => {
                const name = order.type.includes("PICKUP")
                  ? order.pickup?.name
                  : order.dropoff?.name;
                const address = order.type.includes("PICKUP")
                  ? order.pickup?.address
                  : order.dropoff?.address;
                return {
                  label: `${name} - ${address || ""}`,
                  value: order.id,
                };
              })}
              selected={editRouteOrderIds}
              onChange={(orderIds: string[]) => {
                if (editRouteOrderIds.length > orderIds.length) {
                  removeOrder(
                    editRouteOrderIds.find((id) => !orderIds.includes(id))!,
                  );
                }

                useDispatchStore.setState({
                  editRouteOrderIds: orderIds,
                });
              }}
            />
          </Field>
        </div>
        <div className="flex flex-col gap-2 mt-2 mb-4">
          <div className="flex items-center justify-between gap-2">
            <Label>
              {editRouteOrderIds.length}{" "}
              {pluralize("Order", editRouteOrderIds.length)}
              {totalDistance !== "" && (
                <span className="text-xs lg:text-sm text-gray-500 ml-2">
                  {`${totalDistance} miles`}
                </span>
              )}
            </Label>
            <div className="flex items-center gap-2">
              <Tooltip
                text={
                  editRouteOrderIds.length === 0
                    ? "Add orders to the route before computing"
                    : (!isAssigneesValid() && !isOverwriteAssignee) ||
                        !isRouteSequenceValid().isValid ||
                        hasInvalidTimestamps
                      ? "There are errors with the route. Please fix them before computing the route"
                      : "Compute the route, distance, and estimated time"
                }
                position="top"
              >
                <Button
                  icon="fasCog"
                  onClick={() => {
                    drawDirections(false);
                    setShowReorderWarning(false);
                  }}
                  plain
                  disabled={
                    editRouteOrderIds.length === 0 ||
                    (!isAssigneesValid() && !isOverwriteAssignee) ||
                    !isRouteSequenceValid().isValid ||
                    hasInvalidTimestamps ||
                    (!!estimatedPickupTime && !isPickupTimeValid().isValid)
                  }
                >
                  Compute
                </Button>
              </Tooltip>
              <Tooltip
                text={
                  editRouteOrderIds.length === 0
                    ? "Add orders to the route before optimizing"
                    : hasInvalidTimestamps ||
                        orders?.get(editRouteOrderIds[0])?.type !==
                          "ROUTE_OPT_PICKUP"
                      ? "There are errors with the route. Please fix them before optimizing the route"
                      : "Optimize orders based on optimal travel distance and time"
                }
                position="left"
              >
                <Button
                  icon="globe"
                  onClick={() => {
                    setIsOptimizeClicked(true);
                    optimizeOrders();
                    setShowReorderWarning(false);
                  }}
                  isLoading={isOptimizing}
                  disabled={
                    editRouteOrderIds.length === 0 ||
                    orders?.get(editRouteOrderIds[0])?.type !==
                      "ROUTE_OPT_PICKUP" ||
                    hasInvalidTimestamps ||
                    (!!estimatedPickupTime && !isPickupTimeValid().isValid)
                  }
                >
                  Optimize
                </Button>
              </Tooltip>
            </div>
          </div>
          <OrderList
            editRouteOrderIds={editRouteOrderIds}
            routedOrdersDraftEstimatedEta={routedOrdersDraftEstimatedETA}
            orders={orders}
            durations={durations}
            moveOrder={moveOrder}
            removeOrder={removeOrder}
          />
        </div>
      </div>
      <div className="flex-shrink-0 px-4 py-3 bg-white border-t border-gray-200 w-full">
        {editRouteOrderIds.length > 0 && (
          <RouteValidationErrors
            assigneeValidation={getAssigneeValidationState()}
            sequenceValidation={isRouteSequenceValid()}
            workspaceValidation={isWorkspaceValid()}
            isOverwriteAssignee={isOverwriteAssignee}
            setIsOverwriteAssignee={setIsOverwriteAssignee}
            dateTimezoneValidation={isSameDateOrders()}
            assigneeName={assignees?.get(assigneeId || "")?.name}
            hasPotentialDelayedOrders={hasPotentialDelayedOrders}
            hasEarlyDeliveries={hasPotentialEarlyDeliveryOrders}
            invalidTimestampOrders={invalidTimestampOrders}
            pickupTimeValidation={isPickupTimeValid()}
            showReorderWarning={showReorderWarning}
          />
        )}
        <div className="flex gap-2">
          <Tooltip
            text={
              !name.trim()
                ? "Route name is required"
                : editRouteOrderIds.length === 0
                  ? "Add orders to the route before creating"
                  : (!isAssigneesValid() && !isOverwriteAssignee) ||
                      !isRouteSequenceValid().isValid ||
                      !isWorkspaceValid().isValid ||
                      hasInvalidTimestamps
                    ? "There are errors with the route. Please fix them before creating a route"
                    : undefined
            }
            position="top"
          >
            <Button
              onClick={checkAndCreateRoute}
              disabled={
                !name.trim() ||
                editRouteOrderIds.length === 0 ||
                (!isAssigneesValid() && !isOverwriteAssignee) ||
                !isRouteSequenceValid().isValid ||
                !isWorkspaceValid().isValid ||
                !isPickupTimeValid().isValid ||
                !isSameDateOrders() ||
                hasInvalidTimestamps ||
                createRouteDraft.isPending ||
                createRoute.isPending
              }
              isLoading={createRouteDraft.isPending || createRoute.isPending}
            >
              Create
            </Button>
          </Tooltip>
          <Button
            type="button"
            plain
            onClick={() => setIsCreating(false)}
            disabled={createRouteDraft.isPending || createRoute.isPending}
          >
            Cancel
          </Button>
        </div>

        {showHasDelayedOrdersModal && (
          <ConfirmModal
            title={
              <div className="flex items-center gap-4">
                <Icon
                  name="fasCircleExclamation"
                  className="w-6 h-6 text-red-500"
                />
                <span>Confirm route with delay</span>
              </div>
            }
            subtitle="The route will be created with delayed deliveries."
            isOpen
            actions={[
              {
                label: "Confirm & Create",
                onClick: saveRoute,
                size: "sm",
              },
              {
                label: "Cancel",
                onClick: () => setShowHasDelayedOrdersModal(false),
                plain: true,
                size: "sm",
              },
            ]}
            onClose={() => setShowHasDelayedOrdersModal(false)}
          />
        )}
        <ConfirmModal
          title={
            <div className="flex items-center gap-4">
              <Icon
                name="fasCircleExclamation"
                className="w-6 h-6 text-blue-500"
              />
              <span>Confirm route with expected early delivery</span>
            </div>
          }
          subtitle="The route will be created with early deliveries."
          isOpen={showHasEarlyDeliveryOrdersModal}
          actions={[
            {
              label: "Confirm & Create",
              onClick: saveRoute,
              size: "sm",
            },
            {
              label: "Cancel",
              onClick: () => setShowHasEarlyDeliveryOrdersModal(false),
              plain: true,
              size: "sm",
            },
          ]}
          onClose={() => setShowHasEarlyDeliveryOrdersModal(false)}
        />
      </div>
    </div>
  );
}
